import { useState, useEffect } from 'react';

import { ERROR_CODES } from '@vw-marketing/myvw-portal-web-utils/Utils/Helpers/Constants';
import { fetchGoogleMaps } from '../fetch-data/fetch-google-maps';

interface UseGoogleMapsApiProps {
  authService: any;
  localeCode: string;
  baseUrl?: string;
  setActiveErrorModal?: (active: boolean) => void;
  setErrorInformation?: (error: { errorMessage: string }) => void;
}

export const useGoogleMapsApi = ({
  authService,
  localeCode,
  baseUrl,
  setActiveErrorModal,
  setErrorInformation,
}: UseGoogleMapsApiProps) => {
  const [googleAPILoaded, setGoogleAPILoaded] = useState<boolean>(false);
  const [googleAPIUrl, setGoogleAPIUrl] = useState<string | null>(null);

  // Fetch Google API URL
  useEffect(() => {
    const getGoogleData = async () => {
      try {
        const data = await fetchGoogleMaps(
          localeCode,
          baseUrl,
        );

        if (!data) throw new Error('No Google Maps API URL returned');

        setGoogleAPIUrl(data);
      } catch (error) {
        console.error('Error fetching Google Maps API URL:', error);
        setActiveErrorModal && setActiveErrorModal(true);
        setErrorInformation &&
          setErrorInformation({ errorMessage: 'error.google.api' });
        authService.apiLog({
          code: ERROR_CODES.USER_REG.googleIntegrationFailure,
          message: error,
        });
      }
    };

    getGoogleData();
  }, [authService, setActiveErrorModal, setErrorInformation]);

  // Load Google Maps script
  useEffect(() => {
    if (!googleAPIUrl) return;

    if (document.getElementById('google-api')) {
      if (window.google?.maps) setGoogleAPILoaded(true);
      return;
    }

    try {
      const script = document.createElement('script');

      script.type = 'text/javascript';
      script.src = googleAPIUrl;
      script.async = true;
      script.id = 'google-api';

      script.onload = () => {
        setGoogleAPILoaded(true);
      };

      script.onerror = (error: any) => {
        console.error('Error loading Google Maps script:', error);
        setActiveErrorModal && setActiveErrorModal(true);
        setErrorInformation &&
          setErrorInformation({ errorMessage: 'error.google.api' });
      };

      document.body.appendChild(script);
    } catch (error) {
      console.error('Error setting up Google API script:', error);

      setActiveErrorModal && setActiveErrorModal(true);
      setErrorInformation &&
        setErrorInformation({ errorMessage: 'error.google.api' });

      authService.apiLog({
        code: ERROR_CODES.USER_REG.googleIntegrationFailure,
        message: error,
      });
    }

    return () => {
      const scriptToRemove = document.getElementById('google-api');

      if (scriptToRemove) document.body.removeChild(scriptToRemove);
    };
  }, [googleAPIUrl, setActiveErrorModal, setErrorInformation, authService]);

  return { googleAPILoaded };
};
